import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    ViewChild
} from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { map, shareReplay, switchMap, takeUntil } from 'rxjs/operators';
import {
    ApexAxisChartSeries,
    ApexChart,
    ApexDataLabels,
    ApexFill,
    ApexGrid,
    ApexLegend,
    ApexNonAxisChartSeries,
    ApexPlotOptions,
    ApexStroke,
    ApexXAxis,
    ApexYAxis,
    ChartType
} from 'ng-apexcharts';
import {
    AppointmentsApi,
    GetAppointmentStatsRequest,
    GetServiceDurationResult
} from '@dashboard-statistics';
import { StatsService } from '../services/stats.service';

export interface ChartOptions {
    series: ApexAxisChartSeries;
    chart: ApexChart;
    dataLabels: ApexDataLabels;
    plotOptions: ApexPlotOptions;
    stroke: ApexStroke;
    xaxis: ApexXAxis;
    yaxis: ApexYAxis;
    colors: string[];
    fill: ApexFill;
    legend: ApexLegend;
    grid: ApexGrid;
}

export interface PieChartOptions {
    series: ApexNonAxisChartSeries;
    chart: ApexChart;
    labels: string[];
    legend: ApexLegend;
    plotOptions: ApexPlotOptions;
}

@Component({
    selector: 'appointment-summary',
    templateUrl: './summary.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppointmentSummaryComponent implements AfterViewInit, OnDestroy {
    @ViewChild('graphContainer', { static: true }) graphContainer!: ElementRef;

    totalChartOptions$!: Observable<Partial<ChartOptions>>;
    inPersonChartOptions$!: Observable<Partial<ChartOptions>>;
    onlineChartOptions$!: Observable<Partial<ChartOptions>>;
    attendanceTypeChartOptions$!: Observable<Partial<PieChartOptions>>;
    serviceDuration$!: Observable<GetServiceDurationResult>;

    private destroy$ = new Subject<void>();

    constructor(
        private appointmentsApi: AppointmentsApi,
        private statsService: StatsService,
        private cdr: ChangeDetectorRef
    ) { }

    ngAfterViewInit(): void {
        setTimeout(() => {
            const containerH = this.graphContainer.nativeElement.offsetHeight;
            const chartH = Math.max(containerH - 16, 150);

            this.configureStreams(chartH);

            this.cdr.detectChanges();
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private configureStreams(chartHeight: number): void {
        const shared$ = combineLatest([
            this.statsService.period$,
            this.statsService._tenant$
        ]).pipe(
            switchMap(([period, tenant]) => {
                const req = new GetAppointmentStatsRequest();
                req.start = period.periodStart;
                req.end = period.periodEnd;
                req.tenantId = tenant?.id;
                return this.appointmentsApi.stats(req);
            }),
            shareReplay({ bufferSize: 1, refCount: true }),
            takeUntil(this.destroy$)
        );

        // média de duração
        this.serviceDuration$ = shared$.pipe(map(r => r.serviceDuration));

        // gráficos de barra
        this.totalChartOptions$ = shared$.pipe(map(r =>
            this.buildBar(chartHeight,
                r.scheduledExecuted.total.scheduled,
                r.scheduledExecuted.total.executed)));

        this.inPersonChartOptions$ = shared$.pipe(map(r =>
            this.buildBar(chartHeight,
                r.scheduledExecuted.inPerson.scheduled,
                r.scheduledExecuted.inPerson.executed)));

        this.onlineChartOptions$ = shared$.pipe(map(r =>
            this.buildBar(chartHeight,
                r.scheduledExecuted.remotely.scheduled,
                r.scheduledExecuted.remotely.executed)));

        // donut
        this.attendanceTypeChartOptions$ = shared$.pipe(map(r =>
            this.buildDonut(chartHeight, [
                r.scheduledExecuted.inPersonPrivate.executed +
                r.scheduledExecuted.remotelyPrivate.executed,
                r.scheduledExecuted.inPersonInsurance.executed +
                r.scheduledExecuted.remotelyInsurance.executed
            ], ['Particular', 'Convênio'])));
    }

    private buildBar(
        height: number,
        scheduled: number,
        executed: number
    ): Partial<ChartOptions> {
        return {
            series: [
                { name: 'Agendado', data: [scheduled] },
                { name: 'Realizado', data: [executed] }
            ],
            chart: { type: 'bar', height, toolbar: { show: false }, zoom: { enabled: false } },
            colors: ['#fd7f6f', '#bd7ebe'],
            stroke: { width: 1, colors: ['#fff'] },
            xaxis: { categories: [''] },
            dataLabels: { enabled: true },
            yaxis: { show: false },
            legend: { position: 'bottom', horizontalAlign: 'center' },
            grid: { show: false }
        };
    }

    private buildDonut(
        height: number,
        series: number[],
        labels: string[]
    ): Partial<PieChartOptions> {
        return {
            series,
            chart: { type: 'donut', height },
            labels,
            legend: { position: 'bottom', horizontalAlign: 'center' },
            plotOptions: {
                pie: {
                    donut: {
                        size: '70%',
                        labels: { show: true, total: { show: true } }
                    }
                }
            }
        };
    }
}
